import { formatCurrencyAmount } from '@modules/Orders/shared/hooks/useCurrency';
import jsPDF from 'jspdf';
import {
  FulfillmentData,
  OwnBoxPackaging,
  PackagingRateItemData,
  Rate,
  RateItem,
  ShippingData,
  StorageData,
  WeightRange,
} from '../models/rate.types';
import { getCurrentPackagingRateItem, partitionRateItems } from './packagingRateItem';
import { RatePdfLabels } from './ratePdfLabels';
import { extractShippingContext, formatShippingContextSummary } from './shippingContext';

const PAGE = {
  width: 210,
  height: 297,
  margin: 14,
} as const;

interface GenerateRatesComparisonPdfOptions {
  rates: Rate[];
  ownBoxes: OwnBoxPackaging[];
  currency: string;
  labels: RatePdfLabels;
  previewWindow?: Window | null;
}

export function openPdfInNewTab(pdf: jsPDF, previewWindow?: Window | null): void {
  const blob = pdf.output('blob');
  const url = URL.createObjectURL(blob);

  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.href = url;
  } else {
    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      URL.revokeObjectURL(url);
      throw new Error('POPUP_BLOCKED');
    }
  }

  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

class PdfDocumentBuilder {
  private readonly pdf: jsPDF;
  private y: number;

  constructor() {
    this.pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.y = PAGE.margin;
  }

  getPdfDocument(): jsPDF {
    return this.pdf;
  }

  ensureSpace(needed: number): void {
    if (this.y + needed > PAGE.height - PAGE.margin) {
      this.pdf.addPage('a4', 'portrait');
      this.y = PAGE.margin;
    }
  }

  addSectionTitle(text: string): void {
    this.ensureSpace(10);
    this.y += 2;
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(40, 40, 40);
    this.pdf.text(text, PAGE.margin, this.y);
    this.y += 6;
  }

  addSubsectionTitle(text: string): void {
    this.ensureSpace(8);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(60, 60, 60);
    const lines = this.pdf.splitTextToSize(text, PAGE.width - PAGE.margin * 2);
    lines.forEach((line: string) => {
      this.ensureSpace(5);
      this.pdf.text(line, PAGE.margin, this.y);
      this.y += 4.5;
    });
    this.y += 1;
  }

  addContextLine(text: string): void {
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(90, 90, 90);
    const lines = this.pdf.splitTextToSize(text, PAGE.width - PAGE.margin * 2);
    lines.forEach((line: string) => {
      this.ensureSpace(5);
      this.pdf.text(line, PAGE.margin, this.y);
      this.y += 4.5;
    });
    this.y += 2;
  }

  addSpacing(): void {
    this.y += 2;
  }

  drawTable(headers: string[], rows: string[][]): void {
    if (rows.length === 0) return;

    const contentWidth = PAGE.width - PAGE.margin * 2;
    const colCount = headers.length;
    const firstColWidth = Math.min(52, contentWidth * 0.38);
    const otherColWidth = (contentWidth - firstColWidth) / Math.max(colCount - 1, 1);
    const colWidths = [firstColWidth, ...Array(colCount - 1).fill(otherColWidth)];
    const rowHeight = 6;
    const fontSize = colCount > 4 ? 6 : colCount > 3 ? 7 : 8;

    const drawRow = (cells: string[], isHeader: boolean, startY: number): number => {
      let x = PAGE.margin;
      let maxLines = 1;

      const wrappedCells = cells.map((cell, index) => {
        const width = colWidths[index] - 2;
        const lines = this.pdf.splitTextToSize(cell ?? '', width);
        maxLines = Math.max(maxLines, lines.length);
        return lines;
      });

      const actualRowHeight = Math.max(rowHeight, maxLines * 3.5 + 2);

      wrappedCells.forEach((lines, index) => {
        const width = colWidths[index];
        if (isHeader) {
          this.pdf.setFillColor(240, 240, 240);
          this.pdf.rect(x, startY - 4, width, actualRowHeight, 'F');
        }
        this.pdf.setDrawColor(210, 210, 210);
        this.pdf.rect(x, startY - 4, width, actualRowHeight);

        this.pdf.setFont('helvetica', isHeader ? 'bold' : 'normal');
        this.pdf.setFontSize(fontSize);
        this.pdf.setTextColor(isHeader ? 50 : 30, isHeader ? 50 : 30, isHeader ? 50 : 30);

        lines.forEach((line: string, lineIndex: number) => {
          const align = index === 0 ? 'left' : 'center';
          const textX = index === 0 ? x + 1.5 : x + width / 2;
          this.pdf.text(line, textX, startY + lineIndex * 3.5, { align });
        });

        x += width;
      });

      return actualRowHeight;
    };

    this.ensureSpace(rowHeight + 2);
    let rowY = this.y;
    rowY += drawRow(headers, true, rowY);

    rows.forEach((row) => {
      this.ensureSpace(rowHeight + 2);
      if (rowY + rowHeight > PAGE.height - PAGE.margin) {
        this.pdf.addPage('a4', 'portrait');
        rowY = PAGE.margin;
        rowY += drawRow(headers, true, rowY);
      }
      rowY += drawRow(row, false, rowY);
    });

    this.y = rowY + 2;
  }
}

function formatAmount(value: number | undefined | null, currency: string, notApplicable: string): string {
  if (value === undefined || value === null) return notApplicable;
  return formatCurrencyAmount(value, currency);
}

function getShippingItemKey(item: RateItem): string {
  return [
    item.courier_id ?? item.courier_name ?? '',
    item.courier_service_id ?? item.courier_service_name ?? '',
    item.country_id ?? item.country_name ?? '',
    item.courier_zone_id ?? item.courier_zone_name ?? 'none',
    item.title,
  ].join('|');
}

function getShippingContextSummary(item: RateItem): string {
  return formatShippingContextSummary(extractShippingContext(item));
}

function weightRangeKey(range: WeightRange): string {
  return `${range.min_weight.toFixed(3)}|${range.max_weight.toFixed(3)}`;
}

function findShippingItem(rate: Rate, key: string): RateItem | undefined {
  return rate.items.find((item) => item.type === 'shipping' && getShippingItemKey(item) === key);
}

function findFulfillmentItem(rate: Rate): RateItem | undefined {
  return rate.items.find((item) => item.type === 'fulfillment');
}

function findStorageItem(rate: Rate): RateItem | undefined {
  return rate.items.find((item) => item.type === 'storage');
}

function getPackagingData(rate: Rate): PackagingRateItemData | undefined {
  const item = getCurrentPackagingRateItem(rate.items);
  return item?.data as PackagingRateItemData | undefined;
}

function getBoxName(boxId: number, ownBoxes: OwnBoxPackaging[]): string {
  const box = ownBoxes.find((entry) => entry.id === boxId);
  if (!box) return `#${boxId}`;
  return box.code ? `${box.name} (${box.code})` : box.name;
}

function collectShippingKeys(rates: Rate[]): string[] {
  const keys = new Set<string>();
  rates.forEach((rate) => {
    partitionRateItems(rate.items)
      .filter((item) => item.type === 'shipping')
      .forEach((item) => keys.add(getShippingItemKey(item)));
  });
  return Array.from(keys);
}

function collectPackagingBoxIds(rates: Rate[]): number[] {
  const ids = new Set<number>();
  rates.forEach((rate) => {
    const data = getPackagingData(rate);
    data?.items?.forEach((entry) => ids.add(entry.packaging_id));
  });
  return Array.from(ids).sort((a, b) => a - b);
}

function renderShippingSection(
  builder: PdfDocumentBuilder,
  rates: Rate[],
  currency: string,
  labels: RatePdfLabels,
): void {
  const shippingKeys = collectShippingKeys(rates);
  if (shippingKeys.length === 0) return;

  builder.addSectionTitle(labels.sections.shipping);

  const rateHeaders = rates.map((rate) => rate.name);

  shippingKeys.forEach((key) => {
    const referenceItem = rates.map((rate) => findShippingItem(rate, key)).find(Boolean);
    if (!referenceItem) return;

    builder.addSubsectionTitle(referenceItem.title);

    const contextSummary = getShippingContextSummary(referenceItem);
    if (contextSummary) {
      builder.addContextLine(contextSummary);
    }

    const allRangeKeys = new Set<string>();
    rates.forEach((rate) => {
      const item = findShippingItem(rate, key);
      const data = item?.data as ShippingData | undefined;
      data?.weight_ranges?.forEach((range) => allRangeKeys.add(weightRangeKey(range)));
    });

    if (allRangeKeys.size > 0) {
      builder.addSubsectionTitle(labels.shipping.weightRanges);
      const headers = [labels.shipping.weightRangeColumn, ...rateHeaders];
      const rows = Array.from(allRangeKeys)
        .sort()
        .map((rangeKey) => {
          const [min, max] = rangeKey.split('|');
          const label = `${min} - ${max} kg`;
          const values = rates.map((rate) => {
            const item = findShippingItem(rate, key);
            const data = item?.data as ShippingData | undefined;
            const match = data?.weight_ranges?.find((range) => weightRangeKey(range) === rangeKey);
            return formatAmount(match?.amount, currency, labels.notApplicable);
          });
          return [label, ...values];
        });
      builder.drawTable(headers, rows);
    }

    const returnPolicyValues = rates.map((rate) => {
      const item = findShippingItem(rate, key);
      const data = item?.data as ShippingData | undefined;
      if (!data?.return_shipping_policy) return labels.notApplicable;
      if (data.return_shipping_policy === 'same_price') return labels.shipping.returnPolicySamePrice;
      if (data.return_shipping_policy === 'free') return labels.shipping.returnPolicyFree;
      return labels.shipping.returnPolicyCustom;
    });

    if (returnPolicyValues.some((value) => value !== labels.notApplicable)) {
      builder.addSubsectionTitle(labels.shipping.returnPolicy);
      builder.drawTable(['', ...rateHeaders], [[labels.shipping.returnPolicy, ...returnPolicyValues]]);
    }

    const codRows: string[][] = [
      [
        labels.shipping.fixedFee,
        ...rates.map((rate) => {
          const data = findShippingItem(rate, key)?.data as ShippingData | undefined;
          return formatAmount(data?.cash_on_delivery?.fixed_fee, currency, labels.notApplicable);
        }),
      ],
      [
        labels.shipping.percentageFee,
        ...rates.map((rate) => {
          const data = findShippingItem(rate, key)?.data as ShippingData | undefined;
          const fee = data?.cash_on_delivery?.percentage_fee;
          if (fee === undefined || fee === null) return labels.notApplicable;
          return `${(fee * 100).toFixed(2)}%`;
        }),
      ],
    ];

    if (codRows.some((row) => row.slice(1).some((cell) => cell !== labels.notApplicable))) {
      builder.addSubsectionTitle(labels.shipping.codFees);
      builder.drawTable(['', ...rateHeaders], codRows);
    }

    const extraRows: string[][] = [
      [
        labels.shipping.fuelSurcharge,
        ...rates.map((rate) => {
          const data = findShippingItem(rate, key)?.data as ShippingData | undefined;
          const value = data?.extra_charges?.fuel_surcharge_percent;
          if (value === undefined || value === null) return labels.notApplicable;
          return `${value}%`;
        }),
      ],
      [
        labels.shipping.extraWeightCharge,
        ...rates.map((rate) => {
          const data = findShippingItem(rate, key)?.data as ShippingData | undefined;
          return formatAmount(data?.extra_charges?.extra_weight_charge_amount_per_kg, currency, labels.notApplicable);
        }),
      ],
    ];

    if (extraRows.some((row) => row.slice(1).some((cell) => cell !== labels.notApplicable))) {
      builder.addSubsectionTitle(labels.shipping.extraCharges);
      builder.drawTable(['', ...rateHeaders], extraRows);
    }

    builder.addSpacing();
  });
}

function renderFulfillmentSection(
  builder: PdfDocumentBuilder,
  rates: Rate[],
  currency: string,
  labels: RatePdfLabels,
): void {
  const hasFulfillment = rates.some((rate) => findFulfillmentItem(rate));
  if (!hasFulfillment) return;

  builder.addSectionTitle(labels.sections.fulfillment);
  const rateHeaders = rates.map((rate) => rate.name);

  const rows: string[][] = [
    [
      `${labels.fulfillment.packingFee} - ${labels.fulfillment.fixedFee}`,
      ...rates.map((rate) => {
        const data = findFulfillmentItem(rate)?.data as FulfillmentData | undefined;
        return formatAmount(data?.packing_fee?.fixed_fee, currency, labels.notApplicable);
      }),
    ],
    [
      `${labels.fulfillment.packingFee} - ${labels.fulfillment.extraLinesFee}`,
      ...rates.map((rate) => {
        const data = findFulfillmentItem(rate)?.data as FulfillmentData | undefined;
        const fee = data?.packing_fee;
        if (!fee) return labels.notApplicable;
        return `${formatAmount(fee.extra_lines_fee, currency, labels.notApplicable)} (${labels.fulfillment.sinceLineNumber} ${fee.extra_lines_fee_since_line_number})`;
      }),
    ],
    [
      `${labels.fulfillment.packingFee} - ${labels.fulfillment.attachmentFee}`,
      ...rates.map((rate) => {
        const data = findFulfillmentItem(rate)?.data as FulfillmentData | undefined;
        return formatAmount(data?.packing_fee?.attachment_fee, currency, labels.notApplicable);
      }),
    ],
    [
      `${labels.fulfillment.refusedFee} - ${labels.fulfillment.fixedFee}`,
      ...rates.map((rate) => {
        const data = findFulfillmentItem(rate)?.data as FulfillmentData | undefined;
        return formatAmount(data?.reused_fee?.fixed_fee, currency, labels.notApplicable);
      }),
    ],
    [
      `${labels.fulfillment.refusedFee} - ${labels.fulfillment.extraLinesFee}`,
      ...rates.map((rate) => {
        const data = findFulfillmentItem(rate)?.data as FulfillmentData | undefined;
        const fee = data?.reused_fee;
        if (!fee) return labels.notApplicable;
        return `${formatAmount(fee.extra_lines_fee, currency, labels.notApplicable)} (${labels.fulfillment.sinceLineNumber} ${fee.extra_lines_fee_since_line_number})`;
      }),
    ],
  ];

  builder.drawTable(['', ...rateHeaders], rows);
}

function renderStorageSection(
  builder: PdfDocumentBuilder,
  rates: Rate[],
  currency: string,
  labels: RatePdfLabels,
): void {
  const hasStorage = rates.some((rate) => findStorageItem(rate));
  if (!hasStorage) return;

  builder.addSectionTitle(labels.sections.storage);
  const rateHeaders = rates.map((rate) => rate.name);

  builder.drawTable(
    ['', ...rateHeaders],
    [
      [
        labels.storage.fixedFee,
        ...rates.map((rate) => {
          const data = findStorageItem(rate)?.data as StorageData | undefined;
          return formatAmount(data?.fixed_fee, currency, labels.notApplicable);
        }),
      ],
    ],
  );
}

function renderPackagingSection(
  builder: PdfDocumentBuilder,
  rates: Rate[],
  ownBoxes: OwnBoxPackaging[],
  currency: string,
  labels: RatePdfLabels,
): void {
  const boxIds = collectPackagingBoxIds(rates);
  if (boxIds.length === 0) return;

  builder.addSectionTitle(labels.sections.packaging);
  const rateHeaders = rates.map((rate) => rate.name);

  const rows = boxIds.map((boxId) => {
    const boxLabel = getBoxName(boxId, ownBoxes);
    const values = rates.map((rate) => {
      const data = getPackagingData(rate);
      const entry = data?.items?.find((item) => item.packaging_id === boxId);
      return formatAmount(entry?.price, currency, labels.notApplicable);
    });
    return [boxLabel, ...values];
  });

  builder.drawTable([labels.packaging.box, ...rateHeaders], rows);
}

export async function generateRatesComparisonPdf({
  rates,
  ownBoxes,
  currency,
  labels,
  previewWindow,
}: GenerateRatesComparisonPdfOptions): Promise<void> {
  if (rates.length === 0) return;

  const builder = new PdfDocumentBuilder();

  renderShippingSection(builder, rates, currency, labels);
  renderFulfillmentSection(builder, rates, currency, labels);
  renderStorageSection(builder, rates, currency, labels);
  renderPackagingSection(builder, rates, ownBoxes, currency, labels);

  openPdfInNewTab(builder.getPdfDocument(), previewWindow);
}
